<template>
  <div id="top" class="enrollee-page-shell enrollee-content provider-list-page">
    <div class="enrollee-intro">
      <UiPageHeader title="Enrollee List" description="Search active members by client, enrollee identity, and coverage period." />
    </div>
    <section class="enrollee-workbench sba-slide-in" aria-label="Provider enrollee register">
      <div class="enrollee-register-toolbar">
        <div class="enrollee-register-summary" aria-live="polite">
          <strong v-if="isReady">{{ enrollees.length }} active {{ enrollees.length === 1 ? 'member' : 'members' }}</strong>
          <strong v-else>Loading active members</strong>
          <span>Provider monthly register</span>
        </div>
        <button type="button" class="search-toggle" aria-controls="provider-list-search-panel" :aria-expanded="showSearchFilters" @click="toggleSearchFilters">
          <X v-if="showSearchFilters" :size="17" aria-hidden="true" /><ListFilter v-else :size="17" aria-hidden="true" />
          <span>{{ showSearchFilters ? 'Close filters' : 'Filter' }}</span>
        </button>
      </div>
      <div id="provider-list-search-panel" class="enrollee-filter-shell" v-show="showSearchFilters">
        <div class="enrollee-filter-card"><FilterList @search="handleSearch" /></div>
      </div>
      <div class="enrollee-search-summary" :class="{ 'has-loader': isLoading }">
        <span class="summary-label">Current view</span>
        <div class="summary-content" v-if="searchTitle" v-html="searchTitle"></div>
        <div class="summary-content" v-else><span class="summary-chip">All provider enrollees</span></div>
      </div>
      <div class="progress enrollee-progress" v-if="isReady && isLoading"><div class="indeterminate"></div></div>
      <LoadingDiv v-if="!isReady" />
      <div class="enrollee-list-card" v-if="isReady && enrollees.length">
        <div class="enrollee-table-head hide-on-med-and-down"><span>No.</span><span>Member</span><span>Enrollee ID</span><span>Client</span><span>Period</span></div>
        <div class="enrollee-row-card" v-for="(enrollee, index) in enrollees" :key="enrollee.id">
          <div class="enrollee-row-grid">
            <div><span class="enrollee-number">{{ numberBadge(index) }}</span></div>
            <div class="enrollee-person">
              <h6><span>{{ enrollee.name.toUpperCase() }}</span><span class="enrollee-type-text" :class="enrolleeTypeMeta(enrollee.type).className">{{ enrolleeTypeMeta(enrollee.type).label }}</span></h6>
              <div class="enrollee-contact-line">
                <span v-if="ageSex(enrollee.dob, enrollee.sex)">{{ ageSex(enrollee.dob, enrollee.sex)?.toUpperCase() }}</span>
                <span class="contact-dot" v-if="ageSex(enrollee.dob, enrollee.sex) && enrollee.phone"></span>
                <span>{{ enrollee.phone || 'No phone recorded' }}</span>
              </div>
              <div class="enrollee-id-line"><span>Scheme</span><strong>{{ enrollee?.scheme?.name || 'No scheme recorded' }}</strong></div>
            </div>
            <div class="enrollee-data-cell"><span>Enrollee ID</span><p class="enrollee-client">{{ enrollee.enrollee_number || 'No ID recorded' }}</p></div>
            <div class="enrollee-data-cell"><span>Client</span><p class="enrollee-client">{{ enrollee.client_name || 'No client recorded' }}</p></div>
            <div class="enrollee-data-cell"><span>Period</span><p class="enrollee-period">{{ new Date(listMonth).toLocaleString(undefined, { year: 'numeric', month: 'short' }) }}</p></div>
          </div>
        </div>
      </div>
      <div class="enrollee-empty-state" v-if="isReady && !isLoading && !enrollees.length"><h3>No enrollees found</h3><p>No provider enrollee record matched the current selection.</p></div>
      <PaginationDiv v-if="isReady && (enrollees.length || isLoading)" :payLoad="allPages" @newPage="handlePage" />
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import getItems from "@/composables/getItems";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import { ListFilter, X } from "@lucide/vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import FilterList from "@/components/list/FilterListComponent.vue";
import { getCache, addCache } from "@/composables/cache/cache";


export default {
  name: "listHome",
  components: { UiPageHeader, ListFilter, X, LoadingDiv, PaginationDiv, FilterList },
  setup() {
    const response = ref(null);
    const listMonth = ref(null);
    const searchTitle = ref(null);
    const isReady = ref(false);
    const isLoading = ref(true);
    const error = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const showSearchFilters = ref(false);

    // vue router
    const router = useRouter();

    // start
    onMounted(() => {
      // page name
      document.title = "Active (Monthly) Enrollees";
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Enrollees List", title: "Home", icon: null, url: "list", hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Home"));
      // set home
      home.value = tabs.value.find(x => x.title.includes("Home"));
    });

    // handle curr status
    const handleActive = (tab) => {
      // refer
      if (tab.hash) {
        router.push({ name: tab.url, hash: tab.hash });
      } else {
        router.push({ name: tab.url });
      }
    };

    // get function
    const getItemsFromApi = (table) => {

      // get item from store (cache)
      let cache = getCache(table);

      if (cache?.length) {
        response.value = cache;
      }

      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
          // add cache
          addCache(table, response.value);
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    const enrolleeTypeMeta = (type) => {
      if (type === "p") {
        return { label: "Principal", className: "is-principal" };
      }

      if (type === "s") {
        return { label: "Spouse", className: "is-spouse" };
      }

      if (type === "d") {
        return { label: "Dependant", className: "is-dependant" };
      }

      return { label: "Unclassified", className: "is-unclassified" };
    };

    const toggleSearchFilters = () => {
      showSearchFilters.value = !showSearchFilters.value;
    };

    // current month, year
    const getCurrentMonthYear = () => {
      return `${new Date().toLocaleDateString("en", {
        month: "long",
      })} ${new Date().getFullYear()}`;
    };

    // display function
    const ageSex = (dob, sex) => {
      let ageSex;
      let birthDateObj = new Date(dob);
      let today = new Date();
      let age = today.getFullYear() - birthDateObj.getFullYear();

      if (
        today.getMonth() < birthDateObj.getMonth() ||
        (today.getMonth() === birthDateObj.getMonth() &&
          today.getDate() < birthDateObj.getDate())
      ) {
        age--;
      }

      if (dob && sex) {
        ageSex = `(${sex}/${age})`;
      } else if (dob) {
        ageSex = `(${age}yrs)`;
      } else if (sex) {
        ageSex = `(${sex})`;
      }

      return ageSex;
    };

    // handle search (emitted from component)
    const handleSearch = (val) => {
      // start loading
      isLoading.value = true;
      // set current Url
      currUrl.value = val.currUrl;
      // set current Hook
      currHook.value = val.currHook;
      // set listMonth
      listMonth.value = val.listMonth;
      // set search title
      searchTitle.value = val.searchTitle;
      // get items from function
      getItemsFromApi(currUrl.value);
    };

    // switch page
    const handlePage = (page) => {
      // start laoding
      isLoading.value = true;
      // go back up
      router.push({ hash: "#top" });
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
    };

    // output results
    const enrollees = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      ageSex,
      numberBadge,
      enrolleeTypeMeta,
      toggleSearchFilters,
      getCurrentMonthYear,
      handleSearch,
      handlePage,
      isReady,
      isLoading,
      error,
      listMonth,
      searchTitle,
      showSearchFilters,
      enrollees,
      allPages,
    };
  },
};
</script>

<style scoped>

.enrollee-page-shell {
  box-sizing: border-box;
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 2rem 2rem 3rem;
  width: 100%;
}

.enrollee-intro {
  border-bottom: 1px solid var(--ui-border);
  margin-bottom: 2rem;
  padding-bottom: 1.75rem;
}

.enrollee-intro :deep(.ui-page-header) {
  margin-bottom: 0;
}

.enrollee-intro :deep(.ui-page-header__title) {
  font-size: clamp(1.75rem, 2.1vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.025em;
}

.enrollee-intro :deep(.ui-page-header__description) {
  font-size: 1rem;
  line-height: 1.55;
}

.enrollee-add-link,
.search-toggle,
.enrollee-sync-button {
  align-items: center;
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-family: var(--ui-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 0.9rem;
  text-decoration: none;
}

.enrollee-add-link,
.search-toggle {
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  color: var(--ui-primary-foreground);
}

.enrollee-add-link:hover,
.search-toggle:hover {
  filter: brightness(0.95);
}

.enrollee-add-link:focus-visible,
.search-toggle:focus-visible,
.enrollee-sync-button:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.enrollee-workbench {
  background: transparent;
  border: 0;
  box-shadow: none;
  min-width: 0;
}

.enrollee-register-toolbar {
  align-items: center;
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  gap: 1.5rem;
  justify-content: space-between;
  padding-bottom: 1.25rem;
}

.enrollee-register-summary {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.enrollee-register-summary strong {
  color: var(--ui-foreground);
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.4;
}

.enrollee-register-summary span {
  color: var(--ui-muted-foreground);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.enrollee-filter-shell {
  border-bottom: 1px solid var(--ui-border);
  padding: 1.25rem 0;
}

.enrollee-filter-card {
  background: transparent;
  border: 0;
  box-shadow: none;
  padding: 0;
}

.enrollee-filter-card :deep(.sectionx),
.enrollee-filter-card :deep(form) {
  margin: 0;
  padding: 0;
}

.enrollee-filter-card :deep(.sectionx > br) {
  display: none;
}

.enrollee-filter-card :deep(.row) {
  align-items: start;
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin: 0;
}

.enrollee-filter-card :deep(.col) {
  border: 0;
  grid-column: span 2;
  min-width: 0;
  padding: 0 !important;
  width: 100% !important;
}

.enrollee-filter-card :deep(.col:nth-child(n + 4)) {
  grid-column: span 3;
}

.enrollee-filter-card :deep(.col:focus-within) {
  position: relative;
  z-index: 3;
}

.enrollee-filter-card :deep(.col > label) {
  color: var(--ui-foreground);
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 0.4rem;
}

.enrollee-filter-card :deep(nav),
.enrollee-filter-card :deep(.nav-wrapper),
.enrollee-filter-card :deep(.input-field) {
  background: transparent !important;
  box-shadow: none !important;
  height: auto;
  line-height: normal;
  margin: 0;
  min-height: 2.75rem;
  overflow: visible !important;
  width: 100%;
}

.enrollee-filter-card :deep(.input-field) {
  position: relative;
}

.enrollee-filter-card :deep(.input-field > input),
.enrollee-filter-card :deep(.select-wrapper input.select-dropdown) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  box-shadow: none !important;
  box-sizing: border-box;
  color: var(--ui-foreground) !important;
  font-size: 0.875rem;
  font-weight: 400;
  height: 2.75rem !important;
  line-height: 2.75rem;
  margin: 0;
  padding: 0 2.5rem 0 0.75rem !important;
  width: 100%;
}

.enrollee-filter-card :deep(.input-field > input[type="search"]::-webkit-search-cancel-button) {
  display: none;
  -webkit-appearance: none;
}

.enrollee-filter-card :deep(.input-field > input:focus) {
  border-color: var(--ui-ring) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ui-ring) 18%, transparent) !important;
}

.enrollee-filter-card :deep(.input-field > .material-icons) {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--ui-muted-foreground) !important;
  cursor: pointer;
  display: inline-flex;
  font-size: 1.1rem;
  height: 2.75rem;
  justify-content: center;
  line-height: 1;
  margin: 0;
  position: absolute;
  right: 0.25rem;
  top: 0;
  width: 2.25rem;
  z-index: 2;
}

.enrollee-filter-card :deep(.input-field > .material-icons:hover) {
  color: var(--ui-foreground) !important;
}

.enrollee-filter-card :deep(.native-autocomplete) {
  background: var(--ui-popover) !important;
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: var(--ui-shadow-overlay);
  box-sizing: border-box;
  left: 0;
  margin: 0;
  max-height: min(16rem, 50vh);
  min-width: 0;
  overflow-y: auto;
  padding: 0.3rem;
  right: 0;
  top: calc(100% + 0.25rem);
  width: 100%;
  z-index: 220;
}

.enrollee-filter-card :deep(.native-autocomplete li) {
  margin: 0;
  min-height: 0;
}

.enrollee-filter-card :deep(.native-autocomplete li + li) {
  margin-top: 0.125rem;
}

.enrollee-filter-card :deep(.native-autocomplete li > :is(button, span)) {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: calc(var(--ui-radius) - 4px);
  color: var(--ui-popover-foreground) !important;
  cursor: pointer;
  display: flex;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.35;
  min-height: 2.35rem;
  padding: 0.55rem 0.65rem;
  text-align: left;
  width: 100%;
}

.enrollee-filter-card :deep(.native-autocomplete li > button:hover),
.enrollee-filter-card :deep(.native-autocomplete li > button:focus-visible),
.enrollee-filter-card :deep(.native-autocomplete li > button[aria-selected="true"]) {
  background: var(--ui-accent) !important;
  color: var(--ui-accent-foreground) !important;
  outline: 0;
}

.enrollee-filter-card :deep(.mobile-remote-sheet-field) {
  min-height: 2.75rem;
}

.enrollee-filter-card :deep(.mobile-remote-trigger) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  color: var(--ui-foreground) !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  height: 2.75rem !important;
  line-height: normal !important;
  padding: 0 0.25rem 0 0.75rem !important;
}

.enrollee-filter-card :deep(.mobile-remote-trigger-main),
.enrollee-filter-card :deep(.mobile-remote-trigger-icon) {
  height: 2.65rem !important;
  line-height: normal !important;
}

.enrollee-filter-card :deep(.mobile-remote-trigger-icon i) {
  color: var(--ui-muted-foreground) !important;
  height: auto;
  line-height: 1;
}

.enrollee-sync-strip {
  align-items: center;
  border-top: 1px solid var(--ui-border);
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 1.25rem;
  padding-top: 1rem;
}

.enrollee-sync-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.enrollee-sync-button {
  background: var(--ui-background);
  border: 1px solid var(--ui-border);
  color: var(--ui-foreground);
  min-height: 2.25rem;
  padding: 0 0.75rem;
}

.enrollee-sync-button:hover:not(:disabled) {
  background: var(--ui-accent);
  color: var(--ui-accent-foreground);
}

.enrollee-sync-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.enrollee-sync-button.is-syncing svg {
  animation: syncSpin 0.9s linear infinite;
}

.enrollee-sync-meta {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  text-align: right;
}

.enrollee-sync-meta span,
.summary-label {
  color: var(--ui-muted-foreground);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.enrollee-sync-meta strong {
  color: var(--ui-foreground);
  font-size: 0.8125rem;
  font-weight: 600;
}

.enrollee-sync-status {
  background: color-mix(in srgb, var(--ui-success) 9%, var(--ui-background));
  border: 1px solid color-mix(in srgb, var(--ui-success) 24%, var(--ui-border));
  border-radius: calc(var(--ui-radius) - 2px);
  color: var(--ui-success);
  font-size: 0.8125rem;
  margin-top: 1rem;
  padding: 0.65rem 0.75rem;
}

.enrollee-sync-status.has-error {
  background: color-mix(in srgb, var(--ui-destructive) 8%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-destructive) 22%, var(--ui-border));
  color: var(--ui-destructive);
}

.enrollee-search-summary {
  align-items: center;
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  min-height: 3.5rem;
  padding: 0.75rem 0;
}

.summary-content {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: flex-end;
  min-width: 0;
  text-align: right;
}

.enrollee-search-summary :deep(.chip),
.summary-chip {
  background: var(--ui-secondary) !important;
  border: 1px solid var(--ui-border) !important;
  border-radius: 999px !important;
  color: var(--ui-secondary-foreground) !important;
  font-size: 0.75rem !important;
  font-style: normal;
  font-weight: 500 !important;
  height: auto !important;
  line-height: 1.25 !important;
  margin: 0 !important;
  max-width: min(24rem, 100%);
  padding: 0.35rem 0.65rem !important;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.enrollee-search-summary :deep(.chip:first-child) {
  background: transparent !important;
  color: var(--ui-muted-foreground) !important;
}

.enrollee-search-summary :deep(.chip i) {
  font-size: 0.8rem;
  line-height: 1;
  vertical-align: -0.1rem;
}

.enrollee-progress {
  background: transparent;
  border: 0;
  border-radius: 0;
  height: 2px;
  margin: -2px 0 0;
  overflow: hidden;
}

.enrollee-list-card {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  margin: 0;
  overflow: visible;
  padding: 0;
}

.enrollee-table-head,
.enrollee-row-grid {
  align-items: center;
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 3rem minmax(16rem, 1.7fr) minmax(11rem, 1.15fr) minmax(10rem, 1fr) minmax(7rem, 0.65fr);
}

.enrollee-table-head {
  border-bottom: 1px solid var(--ui-border);
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.8rem 0.75rem;
}

.enrollee-row-card {
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-radius: 0;
  box-shadow: none;
  margin: 0;
  padding: 1.15rem 0.75rem;
  transition: background-color 160ms ease;
}

.enrollee-row-card:hover {
  background: color-mix(in srgb, var(--ui-muted) 45%, transparent);
}

.enrollee-number {
  color: var(--ui-muted-foreground);
  display: inline-flex;
  font-size: 0.8125rem;
  font-weight: 600;
  justify-content: flex-start;
}

.enrollee-person,
.enrollee-data-cell {
  min-width: 0;
}

.enrollee-person h6 {
  align-items: center;
  color: var(--ui-foreground);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 0.5rem;
  line-height: 1.4;
  margin: 0;
}

.enrollee-contact-line,
.enrollee-id-line {
  align-items: center;
  color: var(--ui-muted-foreground);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.75rem;
  gap: 0.4rem;
  line-height: 1.4;
  margin-top: 0.3rem;
}

.contact-dot {
  background: var(--ui-border);
  border-radius: 999px;
  display: inline-block;
  height: 0.25rem;
  width: 0.25rem;
}

.enrollee-id-line span,
.enrollee-data-cell > span:first-child {
  color: var(--ui-muted-foreground);
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
}

.enrollee-id-line strong {
  color: var(--ui-foreground);
  font-weight: 600;
}

.enrollee-type-text,
.enrollee-period-flag {
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.25rem 0.45rem;
}

.enrollee-type-text.is-principal {
  background: color-mix(in srgb, var(--ui-success) 10%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-success) 24%, var(--ui-border));
  color: var(--ui-success);
}

.enrollee-type-text.is-spouse {
  background: color-mix(in srgb, var(--ui-primary) 8%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-primary) 20%, var(--ui-border));
  color: var(--ui-primary);
}

.enrollee-type-text.is-dependant {
  background: color-mix(in srgb, var(--ui-warning) 13%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-warning) 28%, var(--ui-border));
  color: color-mix(in srgb, var(--ui-warning) 72%, var(--ui-foreground));
}

.enrollee-type-text.is-unclassified,
.enrollee-period-flag {
  background: var(--ui-secondary);
  color: var(--ui-secondary-foreground);
}

.enrollee-client,
.enrollee-provider,
.enrollee-period {
  color: var(--ui-foreground);
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.enrollee-period {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.enrollee-data-cell > span:first-child {
  display: none;
}

.enrollee-empty-state {
  border-bottom: 1px solid var(--ui-border);
  padding: 3rem 0;
  text-align: center;
}

.enrollee-empty-state h3 {
  color: var(--ui-foreground);
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.enrollee-empty-state p {
  color: var(--ui-muted-foreground);
  font-size: 0.8125rem;
  margin: 0.35rem 0 0;
}

@media (max-width: 1100px) {
  .enrollee-page-shell {
    padding: 1.5rem 1.5rem 2.5rem;
  }

  .enrollee-table-head,
  .enrollee-row-grid {
    gap: 1rem;
    grid-template-columns: 2.5rem minmax(13rem, 1.5fr) minmax(9rem, 1fr) minmax(9rem, 1fr) minmax(6.5rem, 0.7fr);
  }
}

@media (max-width: 900px) {
  .enrollee-filter-card :deep(.row) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .enrollee-filter-card :deep(.col),
  .enrollee-filter-card :deep(.col:nth-child(n + 4)) {
    grid-column: auto;
  }

  .enrollee-table-head {
    display: none;
  }

  .enrollee-row-grid {
    align-items: start;
    grid-template-columns: 2rem minmax(0, 1.35fr) minmax(10rem, 1fr);
  }

  .enrollee-row-grid > :first-child {
    grid-row: 1 / span 4;
  }

  .enrollee-data-cell {
    grid-column: 3;
  }

  .enrollee-data-cell > span:first-child {
    display: block;
    margin-bottom: 0.15rem;
  }
}

@media (max-width: 767px) {
  .enrollee-page-shell {
    padding: 1.25rem 1rem 2rem;
  }

  .enrollee-intro {
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
  }

  .enrollee-add-link {
    width: 100%;
  }

  .enrollee-register-toolbar,
  .enrollee-sync-strip,
  .enrollee-search-summary {
    align-items: stretch;
    flex-direction: column;
  }

  .search-toggle {
    width: 100%;
  }

  .enrollee-sync-meta {
    align-items: flex-start;
    text-align: left;
  }

  .summary-content {
    justify-content: flex-start;
    text-align: left;
  }
}

@media (max-width: 600px) {
  .enrollee-filter-card :deep(.row) {
    grid-template-columns: minmax(0, 1fr);
  }

  .enrollee-sync-actions,
  .enrollee-sync-button {
    width: 100%;
  }

  .enrollee-row-card {
    padding: 1rem 0;
  }

  .enrollee-row-grid {
    grid-template-columns: 1.5rem minmax(0, 1fr);
  }

  .enrollee-person,
  .enrollee-data-cell {
    grid-column: 2;
  }

  .enrollee-data-cell {
    border-top: 1px solid var(--ui-border);
    padding-top: 0.65rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .enrollee-sync-button.is-syncing svg {
    animation: none;
  }
}

@keyframes syncSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

</style>
